/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import StatBlock from "@/components/StatBlock";
import { fmtKg, fmtUsd } from "@/lib/format";

export default function CarbonCost() {
  const { active, refresh } = useProjects();
  const [summary, setSummary] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [selScheme, setSelScheme] = useState("");

  useEffect(() => {
    api.get("/carbon-schemes").then((r) => setSchemes(r.data.items));
  }, []);

  useEffect(() => {
    if (!active) return;
    setSelScheme(active.carbon_price_id);
    api.get(`/projects/${active.id}/summary`).then((r) => setSummary(r.data));
  }, [active]);

  const updateScheme = async (id) => {
    setSelScheme(id);
    try {
      await api.post("/projects", { ...active, carbon_price_id: id }); // not used; we do not have update endpoint
    } catch {
      // intentional: scheme switching is local-only
    }
    // recompute summary locally by switching scheme price
    const s = schemes.find((x) => x.id === id);
    if (s && summary) {
      const newCost = (summary.total_kg / 1000) * s.price_usd_per_t;
      setSummary({ ...summary, carbon_cost_usd: newCost, carbon_price_usd_per_t: s.price_usd_per_t, scheme: s });
    }
  };

  // simple monthly cost projection
  const chartData = (summary?.monthly || []).map((m) => {
    const t = m.total / 1000;
    return {
      month: m.month,
      emissions_t: t,
      cost_usd: t * (summary?.carbon_price_usd_per_t || 0),
    };
  });

  if (!active) return <div className="p-12 theme-ink-muted">Select a project first.</div>;

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Module · Carbon Cost Intelligence"
        title="Carbon Cost"
        subtitle="Connect emissions to commercial exposure via ETS, CBAM, and internal carbon pricing."
        guideId="cost"
      />

      <section className="px-8 pt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatBlock label="Carbon Price" value={`$${summary?.carbon_price_usd_per_t || 0}`} unit="USD/tCO₂e" accent="gold" hint={summary?.scheme?.name} />
        <StatBlock label="Total Emissions" value={fmtKg(summary?.total_kg)} accent="white" />
        <StatBlock label="Exposure" value={fmtUsd(summary?.carbon_cost_usd)} accent="sky" />
        <StatBlock label="At $150/t" value={fmtUsd(((summary?.total_kg || 0) / 1000) * 150)} accent="risk" hint="SBTi-aligned shadow price" />
      </section>

      <section className="px-8 pt-6 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 border border theme-border theme-surface p-6">
          <div className="overline">Monthly cost overlay</div>
          <div className="font-serif-display text-2xl">Emissions × price</div>
          <div className="h-72 mt-3">
            <ResponsiveContainer>
              <ComposedChart data={chartData}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="month" stroke="#71717A" />
                <YAxis yAxisId="left" stroke="#71717A" />
                <YAxis yAxisId="right" orientation="right" stroke="#D4AF37" />
                <Tooltip />
                <Area yAxisId="left" type="monotone" dataKey="emissions_t" fill="#38BDF8" stroke="#38BDF8" fillOpacity={0.15} />
                <Line yAxisId="right" type="monotone" dataKey="cost_usd" stroke="#D4AF37" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border theme-border theme-surface p-6">
          <div className="overline">Pricing scheme</div>
          <div className="font-serif-display text-2xl">Choose scenario</div>
          <div className="space-y-2 mt-4">
            {schemes.map((s) => (
              <button
                key={s.id}
                data-testid={`scheme-${s.id}`}
                onClick={() => updateScheme(s.id)}
                className={`w-full text-left px-4 py-3 border rounded-sm transition-colors ${
                  selScheme === s.id ? "border-gold bg-gold/10" : "border theme-border hover:border-white/30"
                }`}
              >
                <div className="flex items-baseline justify-between">
                  <div className="text-sm theme-ink">{s.name}</div>
                  <div className="font-mono-data text-gold text-sm">${s.price_usd_per_t}/t</div>
                </div>
                <div className="text-[11px] theme-ink-dim mt-1">{s.region} · {s.scope}</div>
              </button>
            ))}
          </div>
        </div>
      </section>
      <div className="px-8 pb-12 text-xs theme-ink-dim mt-4">
        Note: scheme selection is local for scenario exploration; saved scheme reflects the project&apos;s stored pricing choice.
      </div>
    </div>
  );
}
